'use client';

/**
 * AuthCheckpoint Component
 *
 * Modal that appears when user needs to authenticate before proceeding.
 * Shows a message about why auth is required and provides login/signup options.
 *
 * @see Issue #432 - Templated Loan Intake Wizard
 */

import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import Box from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { iconSizes } from '../../../theme/typography';
import { AnimatedModal } from '../../AnimatedModal';
import { GlassButton } from '../../GlassButton';

// ============================================================================
// Types
// ============================================================================

export interface AuthCheckpointProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when user chooses to continue (after auth) */
  onContinue: () => void;
  /** Callback when user chooses to sign in */
  onSignIn?: () => void;
  /** Callback when user chooses to create account */
  onCreateAccount?: () => void;
  /** Name of the next step requiring auth */
  stepName: string;
  /** Custom title */
  title?: string;
  /** Custom message */
  message?: string;
  /** Initial mode to highlight — 'register' emphasizes Create Account, 'login' emphasizes Sign In */
  initialMode?: 'login' | 'register';
}

// ============================================================================
// Styled Components
// ============================================================================

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2, 0),
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background:
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  '& svg': {
    fontSize: iconSizes['2xl'],
    color: theme.palette.primary.main,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.95)' : theme.palette.text.primary,
}));

const Message = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  maxWidth: 400,
  lineHeight: 1.6,
}));

const FeatureList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 320,
  marginBottom: theme.spacing(2),
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1.5),
  borderRadius: 8,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
  '& svg': {
    fontSize: iconSizes.base,
    color: theme.palette.success.main,
  },
}));

const FeatureText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : theme.palette.text.primary,
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 280,
}));

const SavedProgressNote = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.secondary,
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '& svg': {
    fontSize: iconSizes.xs,
  },
}));

// ============================================================================
// Component
// ============================================================================

export const AuthCheckpoint: React.FC<AuthCheckpointProps> = ({
  open,
  onClose,
  onContinue,
  onSignIn,
  onCreateAccount,
  stepName,
  title,
  message,
  initialMode = 'register',
}) => {
  const defaultTitle = 'Account Required';
  const defaultMessage = `To continue with "${stepName}" and protect your sensitive information, please sign in or create an account.`;

  const handleSignIn = () => {
    onSignIn?.();
    // In the actual implementation, this would redirect to auth portal
    // For now, we'll just trigger the continue callback (simulating successful auth)
    onContinue();
  };

  const handleCreateAccount = () => {
    onCreateAccount?.();
    // In the actual implementation, this would redirect to auth portal
    // For now, we'll just trigger the continue callback (simulating successful auth)
    onContinue();
  };

  return (
    <AnimatedModal open={open} onClose={onClose} title="" maxWidth="sm">
      <ContentContainer>
        <IconContainer>
          <LockIcon />
        </IconContainer>

        <Title>{title || defaultTitle}</Title>
        <Message>{message || defaultMessage}</Message>

        <FeatureList>
          <FeatureItem>
            <SecurityIcon />
            <FeatureText>Bank-level encryption for your data</FeatureText>
          </FeatureItem>
          <FeatureItem>
            <SaveIcon />
            <FeatureText>Your progress is automatically saved</FeatureText>
          </FeatureItem>
          <FeatureItem>
            <LockIcon />
            <FeatureText>SSN verification for credit check</FeatureText>
          </FeatureItem>
        </FeatureList>

        <ButtonContainer>
          {initialMode === 'register' ? (
            <>
              <GlassButton variant="gradient" fullWidth onClick={handleCreateAccount} size="large">
                Create Account
              </GlassButton>
              <GlassButton variant="outlined" fullWidth onClick={handleSignIn}>
                Sign In
              </GlassButton>
            </>
          ) : (
            <>
              <GlassButton variant="gradient" fullWidth onClick={handleSignIn} size="large">
                Sign In
              </GlassButton>
              <GlassButton variant="outlined" fullWidth onClick={handleCreateAccount}>
                Create Account
              </GlassButton>
            </>
          )}
        </ButtonContainer>

        <SavedProgressNote>
          <SaveIcon />
          Your progress has been saved and will be restored after sign in.
        </SavedProgressNote>
      </ContentContainer>
    </AnimatedModal>
  );
};

AuthCheckpoint.displayName = 'AuthCheckpoint';

export default AuthCheckpoint;
